<template>
  <RevDrawer
    :close-button-label="i18n(translations.cancel)"
    :name="modalName"
    :title="i18n(translations.title)"
  >
    <template #body>
      <RevInfoBlock
        v-if="apiError"
        class="mt-16"
        :icon="IconWarning"
        :title="apiError.title"
        variant="danger"
      >
        {{ apiError.detail }}
      </RevInfoBlock>

      <RevForm
        :id="FORM_ID"
        :initial-values="INITIAL_VALUES"
        :validate
        @submit="handleSubmit"
      >
        <template #default="{ values, errors }">
          <p class="body-1-bold my-16">
            {{ i18n(translations.prompt, { orderId }) }}
          </p>

          <RevInputDate
            id="pickupDate"
            v-model="values.pickupDate"
            :error="errors.pickupDate"
            :label="i18n(translations.pickupDate)"
            type="date"
          />
        </template>
      </RevForm>
    </template>

    <template #footer>
      <div class="flex flex-col items-center">
        <RevButton
          class="mb-16"
          :form="FORM_ID"
          full-width="always"
          :loading="isLoading"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.confirm) }}
        </RevButton>

        <RevLink @click="handleCancel">
          {{ i18n(translations.cancel) }}
        </RevLink>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { requestNewPickup } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputDate } from '@ds/components/InputDate'
import { RevLink } from '@ds/components/Link'
import { closeModal } from '@ds/components/ModalBase'
import { IconWarning } from '@ds/icons/IconWarning'

import type { Order } from '../../../../../../../../Tab.types'

import {
  FORM_ID,
  INITIAL_VALUES,
  MODAL_NAME,
} from './RequestPickupDrawer.constants'
import translations from './RequestPickupDrawer.translations'
import type { FormValues } from './RequestPickupDrawer.types'

const props = defineProps<{ orderId: Order['id']; refreshOrders: () => void }>()

const i18n = useI18n()
const { openSuccessToast } = useTheToast()

const apiError = ref<null | HttpApiError>(null)
const isLoading = ref(false)
const modalName = computed(() => MODAL_NAME + props.orderId)
const validate = makeValidate<FormValues>({
  pickupDate: required(i18n(translations.required)),
})

async function handleSubmit(value: FormValues) {
  try {
    isLoading.value = true
    apiError.value = null

    await $httpFetch(requestNewPickup, {
      pathParams: {
        orderId: props.orderId,
      },
      body: {
        pickup_date: value.pickupDate,
      },
    })

    openSuccessToast({
      content: i18n(translations.success),
    })

    props.refreshOrders()
    closeModal(modalName.value)
  } catch (error) {
    apiError.value = HttpApiError.fromAnyError(error as Error)
  } finally {
    isLoading.value = false
  }
}

function reset() {
  apiError.value = null
}

function handleCancel() {
  reset()
  closeModal(modalName.value)
}
</script>
